import { scrollPaneToTop } from "@/features/links/DualScrollArea";
import { LinkDetailPanel } from "@/features/links/LinkDetailPanel";
import { LinkPlatformCounts } from "@/features/links/LinkPlatformCounts";
import { LinkTypePie } from "@/features/links/LinkTypePie";
import { LinksFilterBar } from "@/features/links/LinksFilterBar";
import { LinksTable, type SortDir } from "@/features/links/LinksTable";
import { ManualAddDialog } from "@/features/links/ManualAddDialog";
import { MultiSelectFilter } from "@/features/links/MultiSelectFilter";
import { type NoiseBucket, NoiseNotice } from "@/features/links/NoiseNotice";
import { Pagination } from "@/features/links/Pagination";
import { SortPanel } from "@/features/links/SortPanel";
// src/routes/_authed/links.tsx
// IR-1047 — Join Links workspace route.
// Renders in BOTH dev and prod (the IR-922 prod-only build gate is removed); the
// `_authed` layout still enforces is_ops_staff. The page owns sort/dir/page state
// and forwards it (plus per-column filters) to the backend via useLinksPaged
// (IR-1386 — numbered pages replaced the "Load more" infinite list).
import type { LinkUpdatePayload, LinksFilters } from "@/features/links/api";
import {
  downloadLinksCsv,
  useDismissLink,
  useLinkSourceGroups,
  useLinksPaged,
  useResolvePreview,
  useUpdateLink,
} from "@/features/links/api";
import { LINK_COLUMNS } from "@/features/links/column-visibility";
import { useMatchNav } from "@/features/links/useMatchNav";
import { useVisibleColumns } from "@/features/links/useVisibleColumns";
import { useFleetAvatars } from "@/hooks/use-fleet";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/_authed/links")({
  component: LinksPage,
});

function LinksPage() {
  const [filters, setFilters] = useState<LinksFilters>({});
  const [sort, setSort] = useState<string>("created_at");
  const [dir, setDir] = useState<SortDir>("desc");
  // IR-1386 — 1-based page for the numbered pagination.
  const [page, setPage] = useState(1);

  // Anything that changes WHICH rows match must return to page 1: staying on
  // page 7 of a result set that now has 2 pages shows an empty table.
  function onSortChange(column: string, nextDir: SortDir) {
    setSort(column);
    setDir(nextDir);
    setPage(1);
  }

  function onFiltersChange(next: LinksFilters) {
    setFilters(next);
    setPage(1);
  }

  // IR-1386 r2 — the search query is a filter like any other (it goes to the
  // backend as `q`), so it routes through onFiltersChange and therefore still
  // returns to page 1. Only the CONTROL moved out of the filter bar.
  function onSearchChange(q: string) {
    const { q: _q, ...rest } = filters;
    onFiltersChange(q ? { ...rest, q } : rest);
  }

  const {
    items,
    total,
    pageCount,
    grandTotal,
    classificationOptions,
    noiseHidden,
    targetKindCounts,
    platformCounts,
    isLoading,
    isFetching,
    error,
  } = useLinksPaged({ ...filters, sort, dir }, page);

  // IR-1386 — reviewing a hidden bucket = filtering onto it. The backend hides
  // these kinds/statuses only when the corresponding filter is UNSET, so naming
  // one is exactly what brings it back.
  const reviewing: NoiseBucket | null = filters.target_kind?.includes("user")
    ? "user"
    : filters.target_kind?.includes("bot")
      ? "bot"
      : filters.operational_status?.includes("broken")
        ? "broken"
        : null;

  // Both noise filters are dropped before one is re-applied, so switching
  // between buckets never leaves the previous bucket's filter behind (which
  // would AND the two and show an empty table). Omitting the keys rather than
  // setting them undefined is required by exactOptionalPropertyTypes.
  function withoutNoiseFilters(f: LinksFilters): LinksFilters {
    const { target_kind: _kind, operational_status: _status, ...rest } = f;
    return rest;
  }

  function onReviewNoise(bucket: NoiseBucket) {
    const rest = withoutNoiseFilters(filters);
    onFiltersChange(
      bucket === "broken"
        ? { ...rest, operational_status: ["broken"] }
        : { ...rest, target_kind: [bucket] },
    );
  }

  function onBackToQueue() {
    onFiltersChange(withoutNoiseFilters(filters));
  }

  // IR-1233 r3 — Ctrl+F-style navigation across the <mark data-search-match>
  // elements the table renders for the active search query (see useMatchNav).
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { matchCount, activeIdx, goPrev, goNext } = useMatchNav({
    containerRef: tableContainerRef,
    q: filters.q,
    itemCount: items.length,
    // IR-1233 r6 — denominator = every matching row, not just the loaded page.
    totalMatches: total,
    // IR-1386 — "next page" is now a page step, not an append; passing `page`
    // switches the hook to paged semantics so goNext at the last row of a page
    // continues at row 1 of the next one.
    hasNextPage: page < pageCount,
    fetchNextPage: () => setPage((p) => p + 1),
    page,
  });

  // IR-1386 r5 (Tomer): "when moving to another page, list should jump … to the
  // top". The rows live in their own scroll pane (DualScrollArea), which keeps
  // its offset across a page change, so page 4 opened wherever page 3 was left.
  // Only the PAGE BUTTONS reset it — match navigation steps the page too, and
  // there the scroll belongs to the match, not to row 1.
  function onPageButton(next: number) {
    setPage(next);
    scrollPaneToTop(tableContainerRef.current);
  }

  // IR-1233 r3 — "X / Y links" title: `filters` holds ONLY user filters
  // (sort/dir live in separate state), so any key means the view is narrowed.
  const hasActiveFilters = Object.keys(filters).length > 0;

  // IR-1047 (Sean): options for the Avatar (finder) filter. Avatars are stable,
  // so fetch once here rather than riding a per-page response facet.
  const { data: avatars } = useFleetAvatars();
  const avatarFinderOptions = useMemo(
    () => (avatars ?? []).map((a) => ({ value: String(a.id), label: a.name })),
    [avatars],
  );

  // IR-1047 (Rotem 2026-06-15): options for the "Group" (originating source
  // group) filter. Fetched once (stable list), mirroring the avatar options.
  const { data: sourceGroups } = useLinkSourceGroups();
  const sourceGroupOptions = useMemo(
    () => (sourceGroups ?? []).map((g) => ({ value: String(g.id), label: g.name })),
    [sourceGroups],
  );

  // IR-1047 (Rotem 2026-06-15): download the current (filtered + sorted) result
  // set as CSV. The backend exports the FULL filtered set, not just loaded pages.
  const [downloading, setDownloading] = useState(false);
  async function onDownload() {
    setDownloading(true);
    try {
      await downloadLinksCsv({ ...filters, sort, dir });
    } finally {
      setDownloading(false);
    }
  }

  const updateLink = useUpdateLink();
  function onUpdateLink(id: number, patch: LinkUpdatePayload) {
    updateLink.mutate({ id, patch });
  }

  const dismissLink = useDismissLink();
  function onDismiss(id: number) {
    dismissLink.mutate(id);
  }

  // IR-1047 — resolve (fetch on demand) a row's target-group preview. Track the
  // in-flight row id so the table can show that row's button as pending.
  const resolvePreview = useResolvePreview();
  function onResolvePreview(id: number) {
    resolvePreview.mutate(id);
  }
  const resolvingPreviewId =
    resolvePreview.isPending && typeof resolvePreview.variables === "number"
      ? resolvePreview.variables
      : null;

  const [addOpen, setAddOpen] = useState(false);

  // IR-1238 persisted column selection + IR-1233 r6 Source-Groups auto-reveal
  // (Rotem: "117 results but no match/highlight and no apparent reason").
  const { visibleCols, tableCols, sourceGroupsAutoRevealed, onColumnsChange } = useVisibleColumns({
    q: filters.q,
    rows: items,
  });

  // IR-1047 (Rotem 2026-06-22) — click a row to open the target-group detail
  // panel (large picture + name/bio/members). The selected row is resolved from
  // the current page set, so it stays in sync as previews resolve / data updates.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selectedRow = useMemo(
    () => items.find((r) => r.id === selectedId) ?? null,
    [items, selectedId],
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <h1 className="text-2xl font-semibold text-white">Join Links</h1>
          {!isLoading &&
            // IR-1233 r3 — "X / Y links" while filtered (X = filtered total,
            // Y = unfiltered grand total); plain count otherwise or when the
            // backend does not send grand_total yet.
            (hasActiveFilters && grandTotal != null ? (
              <span className="text-sm text-[var(--iris-fg-3)]">
                <strong className="text-[var(--iris-fg-1)]">{total.toLocaleString()}</strong> /{" "}
                {grandTotal.toLocaleString()} links
              </span>
            ) : (
              <span className="text-sm text-[var(--iris-fg-3)]">
                <strong className="text-[var(--iris-fg-1)]">{total.toLocaleString()}</strong> link
                {total === 1 ? "" : "s"}
              </span>
            ))}
        </div>
        <div className="flex items-center gap-2">
          <MultiSelectFilter
            label="Columns"
            options={LINK_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
            selected={visibleCols}
            onApply={onColumnsChange}
            searchable
          />
          <button
            type="button"
            className="iris-btn text-sm"
            onClick={onDownload}
            disabled={downloading || isLoading}
            aria-label="Download CSV"
          >
            {downloading ? "Preparing…" : "Download"}
          </button>
          <button
            type="button"
            className="iris-btn iris-btn--primary text-sm"
            onClick={() => setAddOpen(true)}
          >
            Add link
          </button>
        </div>
      </div>
      <ManualAddDialog open={addOpen} onOpenChange={setAddOpen} />
      <LinksFilterBar
        value={filters}
        onChange={onFiltersChange}
        classificationOptions={classificationOptions}
        avatarFinderOptions={avatarFinderOptions}
        sourceGroupOptions={sourceGroupOptions}
        // IR-1386 r3 (Rotem): the type pie shares the filter row. r4 adds the
        // per-platform counts under it, in the same box — one "what is in the
        // queue right now" block rather than two competing ones.
        aside={
          <LinkTypePie
            counts={targetKindCounts}
            footer={<LinkPlatformCounts counts={platformCounts} />}
          />
        }
      />
      {/* IR-1386 r2 (Rotem) — Search leads the sort row, not the filter grid. */}
      <SortPanel
        sort={sort}
        dir={dir}
        onChange={onSortChange}
        search={filters.q ?? ""}
        onSearchChange={onSearchChange}
        matchNav={{
          // Displayed 1-based; activeIdx -1 (nothing active yet) renders as 0.
          current: activeIdx + 1,
          total: matchCount,
          onPrev: goPrev,
          onNext: goNext,
        }}
      />
      {isLoading ? (
        <div className="p-6 text-sm text-[var(--iris-fg-3)]">Loading…</div>
      ) : error ? (
        <div className="p-6 text-sm text-[var(--iris-neon-pink)]">Failed to load links</div>
      ) : (
        <>
          {/* IR-1233 r6 — say WHY a column the analyst did not pick appeared,
              so the reveal reads as an answer rather than a glitch. */}
          {sourceGroupsAutoRevealed && (
            <p className="text-xs text-[var(--iris-fg-3)]">
              Source Groups shown automatically — your search matches group names.
            </p>
          )}
          {/* IR-1386 — never hide the noise silently: say how much was held
              back and make every bucket one click from review. */}
          <NoiseNotice
            noise={noiseHidden}
            reviewing={reviewing}
            onReview={onReviewNoise}
            onBackToQueue={onBackToQueue}
          />
          {/* IR-1233 r3 — the match-navigation DOM registry scans this wrapper. */}
          <div ref={tableContainerRef}>
            <LinksTable
              rows={items}
              sort={sort}
              dir={dir}
              onSortChange={onSortChange}
              onUpdateLink={onUpdateLink}
              onDismiss={onDismiss}
              onResolvePreview={onResolvePreview}
              resolvingPreviewId={resolvingPreviewId}
              onRowClick={setSelectedId}
              highlightQuery={filters.q}
              visibleCols={tableCols}
            />
          </div>
          {/* IR-1386 (Tomer) — numbered pages replace the "Load more" button.
              r5 (Tomer): a page turn also returns the list to row 1 — see
              onPageButton. */}
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={onPageButton}
            busy={isFetching}
          />
        </>
      )}
      <LinkDetailPanel
        row={selectedRow}
        open={selectedId != null}
        onOpenChange={(o) => {
          if (!o) setSelectedId(null);
        }}
        onResolvePreview={onResolvePreview}
        resolvingPreviewId={resolvingPreviewId}
      />
    </div>
  );
}
